import { useState, useEffect, useCallback } from 'react';

export interface DriveImage {
  filename: string;
  title: string;
  url: string;
  width?: number;
  height?: number;
}

interface Manifest {
  lastSyncedAt: string;
  categories: string[];
  imagesByCategory: Record<string, DriveImage[]>;
}

// Client-exposed expected key (prefer Vite; fallback Next public)
const EXPECTED_SYNC_KEY: string =
  (import.meta as any)?.env?.VITE_SUPABASE_ANON_KEY ||
  (typeof process !== 'undefined' && (process as any).env?.NEXT_PUBLIC_SUPABASE_ANON_KEY) ||
  '';


export const useGalleryAPI = () => {
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchManifest = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('https://vsyvxyfuqrcuytpsrzbt.functions.supabase.co/gallery-manifest');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      setManifest(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch gallery');
      console.error('Gallery fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchManifest();
  }, [fetchManifest]);

  const getImagesByCategory = useCallback((category: string): DriveImage[] => {
    if (!manifest) return [];
    return manifest.imagesByCategory[category] || [];
  }, [manifest]);

  const getCategories = useCallback((): string[] => {
    return manifest?.categories || [];
  }, [manifest]);

  return {
    manifest,
    loading,
    error,
    getImagesByCategory,
    getCategories,
    refetch: fetchManifest,
    lastSyncedAt: manifest?.lastSyncedAt,
  };
};

export const useGallerySync = () => {
  const [isLoading, setIsLoading] = useState(false);

  const syncGallery = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const FN = 'https://vsyvxyfuqrcuytpsrzbt.functions.supabase.co';

      // Ask every time; do not store locally so a wrong key never “persists”
      const input = (typeof window !== 'undefined'
        ? window.prompt('Enter Gallery Sync password:')
        : ''
      );
      const pwd = (input ?? '').trim();

      // Cancelled / empty → do nothing (no request fired)
      if (!pwd) return;

      const res = await fetch(`${FN}/gallery-sync`, {
        method: 'POST',
        headers: { 'x-sync-key': pwd },
      });

      // Wrong password → stop here
      if (res.status === 401 || res.status === 403) {
        if (typeof window !== 'undefined') {
          alert('Incorrect password.');
        }
        return;
      }

      const data = await res.json().catch(() => ({}));

      if (res.status === 429) throw new Error('Sync cooling down. Try again in ~60s.');
      if (!res.ok) throw new Error(data.error || data.details || `Sync failed (${res.status})`);

      return data;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    syncGallery,
    isLoading,
  };

};